import clsx from "clsx";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import type { FindPackageByIdCountry } from "@/types/codegen";

type Props = {
  countries: FindPackageByIdCountry[];
};

export function VisaPermissionsCard({ countries }: Props) {
  const intl = useIntl();

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        id: "trips.visaPermissions.title",
        defaultMessage: "Visa permissions",
      })}
      subtitle={intl.formatMessage({
        id: "trips.visaPermissions.subtitle",
        defaultMessage: "Visa requirements and assistance details",
      })}
      collapsible={false}
    >
      <ul className="space-y-2">
        {countries?.map(({ id, name, visaType }) => {
          const getVisaTranslation = () => {
            switch (visaType) {
              case "E_VISA":
                return intl.formatMessage({
                  id: "trips.visaTypes.eVisa",
                  defaultMessage: "e-Visa",
                });
              case "VISA_ON_ARRIVAL":
                return intl.formatMessage({
                  id: "trips.visaTypes.visaOnArrival",
                  defaultMessage: "Visa on arrival",
                });
              case "VISA_FREE":
                return intl.formatMessage({
                  id: "trips.visaTypes.visaFree",
                  defaultMessage: "Visa free",
                });
              case "VISA_REQUIRED":
                return intl.formatMessage({
                  id: "trips.visaTypes.visaRequired",
                  defaultMessage: "Visa required",
                });
              default:
                return visaType; // Fallback to raw value if no translation is found
            }
          };

          return (
            <li
              key={id}
              className={clsx(
                "flex items-center justify-between rounded-xl p-3 text-sm font-medium text-text-primary",
                visaType === "VISA_FREE"
                  ? "bg-surface-accent-softer"
                  : visaType === "VISA_REQUIRED"
                    ? "bg-semantic-error-pressed-soft"
                    : "bg-semantic-warning-soft",
              )}
            >
              <span>{name}</span>
              <span>{getVisaTranslation()}</span>
            </li>
          );
        })}
      </ul>
    </CollapsibleCard>
  );
}
